import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useState } from 'react';
import { X, Loader } from 'react-feather';
import { omit } from '../../utils/omit.js';
import defaultStyles from './styles/Tag.module.scss.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { VisuallyHidden } from '../visuallyHidden/VisuallyHidden.js';
import { Enter } from '../../utils/keycode.js';

const COLORS = ['default', 'critical', 'pending', 'success', 'info', 'warning', 'disabled'];
const Tag = /*#__PURE__*/forwardRef(({
  theme,
  children,
  color = 'default',
  closable,
  checkable,
  defaultChecked,
  size = 'md',
  onClose,
  onCheck,
  icon,
  closeIcon,
  bordered,
  ...rest
}, ref) => {
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const [visible, setVisible] = useState('visible' in rest ? rest.visible : true);
  const [checked, setChecked] = useState('checked' in rest ? rest.checked : defaultChecked);
  const [loading, setLoading] = useState();

  // controlled
  const mergedChecked = 'checked' in rest ? rest.checked : checked;
  const mergedVisible = 'visible' in rest ? rest.visible : visible;
  function onHandleClose(e) {
    const ret = onClose && onClose(e);
    if (ret && ret.then) {
      setLoading(true);
      ret.then(() => {
        setLoading(false);
        setVisible(false);
      }).catch(() => {
        setLoading(false);
      });
    } else {
      setVisible(false);
    }
  }
  function onHandleCheck() {
    const newChecked = !mergedChecked;
    if (!('checked' in rest)) {
      setChecked(newChecked);
    }
    onCheck && onCheck(newChecked);
  }
  const getColor = COLORS.indexOf(color) !== -1 ? color : '';
  const _color = color ? getColor : '';
  const _checked = checkable ? mergedChecked : false;
  const colorClasses = clsx({
    [styles.default]: _color === 'default',
    [styles.critical]: _color === 'critical',
    [styles.pending]: _color === 'pending',
    [styles.success]: _color === 'success',
    [styles.info]: _color === 'info',
    [styles.warning]: _color === 'warning',
    [styles.disabled]: _color === 'disabled',
    [styles.custom]: _checked && color && !_color
  });
  const sizeClasses = clsx({
    [styles.xs]: size === 'xs',
    [styles.sm]: size === 'sm',
    [styles.md]: size === 'md',
    [styles.lg]: size === 'lg'
  });
  const tagClassNames = clsx(styles.tag, {
    [styles.loading]: loading,
    [styles.hide]: !mergedVisible,
    [styles.checkable]: checkable,
    [styles.checked]: _checked,
    [styles.bordered]: bordered
  }, sizeClasses, colorClasses);
  const otherProps = omit(rest, ['visible', 'theme']);
  const handleKeyDown = (e, callback) => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === Enter.code) {
      callback();
    }
  };
  const clickableTagProps = checkable ? {
    role: 'button',
    tabIndex: 0,
    onKeyDown: e => {
      if (checkable) {
        handleKeyDown(e, onHandleCheck);
      }
    },
    onClick: () => {
      if (checkable) {
        onHandleCheck && onHandleCheck();
      }
    }
  } : {};
  return /*#__PURE__*/jsxs("span", {
    ref: ref,
    "data-test-id": "tag",
    className: tagClassNames,
    ...clickableTagProps,
    ...otherProps,
    children: [icon && /*#__PURE__*/jsx("span", {
      "data-test-id": "tag-icon",
      className: styles.icon,
      children: icon
    }), children, closable && !loading && closeIcon !== null && /*#__PURE__*/jsxs("button", {
      type: "button",
      tabIndex: 0,
      "data-test-id": "tag-close",
      className: styles.close_btn,
      onClick: onHandleClose,
      onKeyDown: event => {
        handleKeyDown(event, onHandleClose);
      },
      children: [/*#__PURE__*/jsx(VisuallyHidden, {
        children: "Close"
      }), closeIcon !== undefined ? closeIcon : /*#__PURE__*/jsx(X, {})]
    }), loading && /*#__PURE__*/jsx("span", {
      "data-test-id": "tag-loader",
      className: styles.loading_icon,
      children: /*#__PURE__*/jsx(Loader, {})
    })]
  });
});
Tag.displayName = 'Tag';

export { Tag };
